import logger from '3-foundation/Logging/logger';
import { useSiteContext } from '3-foundation/Site/SiteContext';
import { capitalizeFirstLetter } from '3-foundation/Helpers/capitalizeFirstLetter';
import { NODE_ENV } from '3-foundation/_constants';
import dynamic from 'next/dynamic';

// Dynamic import can't be used inside of React rendering as it needs to be marked in the top level of the module for preloading to work, similar to React.lazy.
// https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading#examples

// Don't get tempted to dynamically generate the imports, eg. with a forEach loop and template literals or similar.
// Tried and tested, it just wont work. It fails silently by disabling SSR and initial hydration of data!
// From the documentation: In import('path/to/component'), the path must be explicitly written. It can't be a template string nor a variable.

// Add a new page type by adding it to the imports
const pages = {
	ArticlePage: dynamic(() => import('./ArticlePage').then((module) => module['ArticlePage'])),
	ContentPage: dynamic(() => import('./ContentPage').then((module) => module['ContentPage'])),
	HomePage: dynamic(() => import('./HomePage').then((module) => module['HomePage'])),
	NewsCatalogPage: dynamic(() => import('./NewsCatalogPage').then((module) => module['NewsCatalogPage'])),
	NewsPage: dynamic(() => import('./NewsPage').then((module) => module['NewsPage'])),
	DocumentPage: dynamic(() => import('./DocumentPage').then((module) => module['DocumentPage'])),
	DocumentCatalogPage: dynamic(() => import('./DocumentCatalogPage').then((module) => module['DocumentCatalogPage'])),
	EventPage: dynamic(() => import('./EventPage').then((module) => module['EventPage'])),
	EventCatalogPage: dynamic(() => import('./EventCatalogPage').then((module) => module['EventCatalogPage'])),
	SearchPage: dynamic(() => import('./SearchPage').then((module) => module['SearchPage'])),
	SubjectPage: dynamic(() => import('./SubjectPage').then((module) => module['SubjectPage'])),
	OrganisationPage: dynamic(() => import('./OrganisationPage').then((module) => module['OrganisationPage'])),
	ContactPersonPage: dynamic(() => import('./ContactPersonPage').then((module) => module['ContactPersonPage'])),
	JobListPage: dynamic(() => import('./JobListPage').then((module) => module['JobListPage'])),
};

export const DynamicPage = (page: Content.Page) => {
	const production = NODE_ENV === 'production';
	const site = useSiteContext();
	const pageName = `${capitalizeFirstLetter(page.documentType)}`;

	const Page = pages[pageName];

	if (!pageName) {
		const message = `PageType [${page.documentType}] is missing a React Component. Add missing export '${pageName}' from /1-project/Pages`;
		logger.error(message, { siteHost: site?.host });
		return !production ? <div>{message}</div> : null;
	}

	return <Page {...page} />;
};
